import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApi } from '../hooks/useApi';
import { useWebSocket } from '../hooks/useWebSocket';
import s from '../components/chat/chatStyles';
import ConversationList from '../components/chat/ConversationList';
import ConversationView, { EmptyState, QueuedPanel } from '../components/chat/ConversationView';
import { STATUS } from '../components/chat/status';
import StartConversationModal from '../components/templates/StartConversationModal';
import { notifyNewCase, notifyReply, requestNotificationPermission } from '../utils/notify';
import Composer from '../components/chat/Composer';
import TransferModal from '../components/chat/TransferModal';
import ThemeToggle from '../theme/ThemeToggle';
import { Users, Radio, Mail, KeyRound, LogOut, Search, X, Bell, SlidersHorizontal, ChevronDown } from 'lucide-react';

// ─── Componente principal ─────────────────────────────────────────────────────
export default function ChatPage({ onOpenAdmin, onOpenStudio, onChangePassword }) {
  const { user, logout } = useAuth();
  // Solo para mostrar u ocultar controles; el backend valida el rol en cada acción
  const isAdmin = user?.role === 'admin';
  const { apiFetch } = useApi();

  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [search, setSearch] = useState('');
  const [unread, setUnread] = useState({});
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  const [allTags, setAllTags] = useState([]);
  const [tagFilter, setTagFilter] = useState(null);
  const [tagPickerFor, setTagPickerFor] = useState(null);

  // Fase 4: asignación
  const [statusFilter, setStatusFilter] = useState('');     // '' = todas (filtro del servidor)
  // Archivados (personales): la lista principal los oculta hasta que el cliente escribe
  const [showArchived, setShowArchived] = useState(false);
  const showArchivedRef = useRef(false);
  showArchivedRef.current = showArchived;
  const [assignableUsers, setAssignableUsers] = useState([]); // admin: usuarios con su carga
  const [toast, setToast] = useState(null);
  const [taking, setTaking] = useState(false);
  // Fase 6: presencia propia (online = Disponible, paused = Pausa)
  const [myPresence, setMyPresence] = useState(null);
  // Fase 7: enviar plantilla (null | { phone?: string })
  const [templateModal, setTemplateModal] = useState(null);
  // Punto 1: conversación que se está transfiriendo (null = modal cerrado)
  const [transferConv, setTransferConv] = useState(null);

  // La ventana de 24 h se lee de la lista (se actualiza por WebSocket y cada 15 s)
  const selectedConv = selected ? (conversations.find(c => c.session_id === selected.session_id) || selected) : null;
  // Escribir, etiquetar, cerrar, transferir: el admin o el asesor que tiene el caso
  const canOperate = !!selectedConv && (isAdmin || (selectedConv.status === 'assigned' && selectedConv.assigned_user?.id === user?.id));
  const windowClosed = selectedConv?.canal === 'whatsapp' && selectedConv?.window_open === false;

  const messagesEndRef = useRef(null);
  const convsRef = useRef([]);
  convsRef.current = conversations;
  const selectedRef = useRef(null);
  const textareaRef = useRef(null);
  const [dateFilter, setDateFilter] = useState({ desde: '', hasta: '' });
  // Rango de fechas aplicado (el panel edita un borrador hasta "Aplicar")
  const [appliedDates, setAppliedDates] = useState({ desde: '', hasta: '' });
  // Panel de filtros plegable; se recuerda si el usuario lo deja abierto
  const [filtersOpen, setFiltersOpen] = useState(() => {
    try { return localStorage.getItem('chat_filters_open') === '1'; } catch { return false; }
  });
  selectedRef.current = selected;
  const userRef = useRef(user);
  userRef.current = user;
  const refreshRef = useRef(() => {});

  // ─── No leídos (solo en los casos que tengo asignados) ─────────────────────
  // El servidor los guarda por usuario: sobreviven a recargas y se sincronizan
  // entre pestañas (evento conversation_read).
  const isMine = useCallback(c => !!c && c.status === 'assigned' && c.assigned_user?.id === userRef.current?.id, []);
  const mergeUnread = useCallback(data => {
    setUnread(prev => {
      const next = { ...prev };
      data.forEach(c => {
        const reading = c.session_id === selectedRef.current?.session_id && !document.hidden;
        next[c.session_id] = reading ? 0 : (c.unread || 0);
      });
      return next;
    });
  }, []);
  const markRead = useCallback(sessionId => {
    setUnread(prev => (prev[sessionId] ? { ...prev, [sessionId]: 0 } : prev));
    apiFetch(`/api/conversations/${sessionId}/read`, { method: 'POST' }).catch(() => {});
  }, [apiFetch]);
  const markReadRef = useRef(markRead);
  markReadRef.current = markRead;

  // ─── Cargar datos ─────────────────────────────────────────────────────────
  // ─── Lista paginada ─────────────────────────────────────────────────────────
  // Páginas de 50 (la más reciente primero). La búsqueda por número, el estado,
  // la etiqueta y las fechas se aplican en el servidor sobre todas las conversaciones.
  const PAGE = 50;
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [serverQ, setServerQ] = useState('');

  // El buscador consulta al servidor (número o nombre) con espera de 300 ms
  useEffect(() => {
    const text = search.trim();
    const digits = text.replace(/\D/g, '');
    let q = '';
    if (/^[\d+\s-]+$/.test(text)) q = digits.length >= 3 ? digits : '';
    else if (text.length >= 2) q = text.slice(0, 60);
    const t = setTimeout(() => setServerQ(q), 300);
    return () => clearTimeout(t);
  }, [search]);

  const listQuery = useCallback((df, cursor) => {
    const filters = df || dateFilter;
    const params = new URLSearchParams();
    if (filters.desde) params.set('desde', filters.desde);
    if (filters.hasta) params.set('hasta', filters.hasta);
    if (statusFilter) params.set('status', statusFilter);
    if (showArchived) params.set('archived', '1');
    if (tagFilter) params.set('tag_id', String(tagFilter));
    if (serverQ) params.set('q', serverQ);
    params.set('limit', String(PAGE));
    if (cursor) { params.set('before_at', cursor.last_at || '1970-01-01T00:00:00Z'); params.set('before_id', cursor.session_id); }
    return `/api/conversations?${params}`;
  }, [dateFilter, statusFilter, showArchived, tagFilter, serverQ]);

  /**
   * Primera página. reset=true cuando cambian los filtros; si no (refresco
   * periódico), se conservan las páginas más antiguas ya cargadas.
   */
  const loadConversations = useCallback(async (df, { reset = true } = {}) => {
    try {
      const { data, total: t } = await apiFetch(listQuery(df), { withTotal: true });
      const byDate = !!((df || dateFilter).desde || (df || dateFilter).hasta);
      setTotal(t ?? (byDate ? data.length : null));
      mergeUnread(data);
      setConversations(prev => {
        if (reset) return data;
        const fresh = new Set(data.map(c => c.session_id));
        const older = prev.filter(c => !fresh.has(c.session_id));
        return [...data, ...older].sort((a, b) => new Date(b.last_at || 0) - new Date(a.last_at || 0));
      });
      if (reset) setHasMore(!byDate && data.length === PAGE);
      return data;
    } catch {}
  }, [apiFetch, listQuery, dateFilter, mergeUnread]);

  // Sale de la vista actual (archivado o recuperado): también baja el total
  const dropFromList = useCallback(sessionId => {
    if (!convsRef.current.some(c => c.session_id === sessionId)) return;
    setConversations(prev => prev.filter(c => c.session_id !== sessionId));
    setTotal(t => (t ? t - 1 : t));
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || conversations.length === 0) return;
    setLoadingMore(true);
    try {
      const last = conversations[conversations.length - 1];
      const data = await apiFetch(listQuery(null, last));
      mergeUnread(data);
      setConversations(prev => {
        const seen = new Set(prev.map(c => c.session_id));
        return [...prev, ...data.filter(c => !seen.has(c.session_id))];
      });
      setHasMore(data.length === PAGE);
    } catch {} finally {
      setLoadingMore(false);
    }
  }, [apiFetch, listQuery, conversations, hasMore, loadingMore, mergeUnread]);

  const loadTags = useCallback(async () => {
    try {
      const data = await apiFetch('/api/tags');
      setAllTags(data);
    } catch {}
  }, [apiFetch]);

  const loadAssignable = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await apiFetch('/api/admin/workload');
      setAssignableUsers(data.users);
    } catch {}
  }, [apiFetch, isAdmin]);

  useEffect(() => { requestNotificationPermission(); }, []);

  // El aviso de caso nuevo se oculta solo
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 10000);
    return () => clearTimeout(t);
  }, [toast]);

  const loadMessages = useCallback(async (session_id) => {
    setLoadingMsgs(true);
    try {
      const data = await apiFetch(`/api/conversations/${session_id}/messages`);
      setMessages(Array.isArray(data) ? data : []);
    } catch {} finally {
      setLoadingMsgs(false);
    }
  }, [apiFetch]);

  // Filtros nuevos → lista desde la primera página
  useEffect(() => { loadConversations(); }, [loadConversations]);

  // Refresco periódico: solo la primera página, sin perder lo ya cargado
  const refreshListRef = useRef(() => {});
  const lastListRefresh = useRef(0);
  refreshListRef.current = () => loadConversations(null, { reset: false });
  useEffect(() => {
    loadTags();
    loadAssignable();
    const t = setInterval(() => { refreshListRef.current(); loadAssignable(); }, 15000);
    return () => clearInterval(t);
  }, [loadTags, loadAssignable]);

  // Asesor técnico con AGENT_SEES_SARA_CHATS: también ve los chats de SARA de sus clientes
  // (asesor anterior; con AGENT_SARA_CHATS_SCOPE=all, todos). Filtro Mis casos / SARA
  const [seesSara, setSeesSara] = useState(false);
  // Minutos sin respuesta a partir de los que la espera del cliente se marca en rojo
  const [replyAlertMinutes, setReplyAlertMinutes] = useState(5);
  useEffect(() => {
    apiFetch('/api/features').then(f => {
      if (!isAdmin) setSeesSara(!!f.sees_sara_chats);
      if (f.reply_alert_minutes != null) setReplyAlertMinutes(f.reply_alert_minutes);
    }).catch(() => {});
  }, [apiFetch, isAdmin]);

  function applyDateFilter(desde, hasta) {
    const nf = { desde, hasta };
    setDateFilter(nf);
    setAppliedDates(nf);
    loadConversations(nf);
  }

  function clearDateFilter() {
    applyDateFilter('', '');
  }

  function toggleFilters() {
    setFiltersOpen(open => {
      try { localStorage.setItem('chat_filters_open', open ? '0' : '1'); } catch {}
      return !open;
    });
  }

  function clearAllFilters() {
    setStatusFilter('');
    setTagFilter(null);
    if (appliedDates.desde || appliedDates.hasta) clearDateFilter();
  }

  useEffect(() => {
    if (selected && !(selected.status === 'queued' && !isAdmin)) loadMessages(selected.session_id);
  }, [selected, loadMessages, isAdmin]);

  // ─── WebSocket ────────────────────────────────────────────────────────────
  const wsStatus = useWebSocket(useCallback((msg) => {
    // El backend solo envía eventos de conversaciones que este usuario puede ver
    if (msg.type === 'connected') {
      if (msg.data?.presence) setMyPresence(msg.data.presence);
      return;
    }
    if (msg.type === 'presence_changed') {
      if (msg.data.user_id === userRef.current?.id) setMyPresence(msg.data.status);
      if (userRef.current?.role === 'admin') refreshRef.current();
      return;
    }
    if (msg.type === 'conversation_queued') {
      // Solo llega a los admins: SARA escaló un caso y espera asignación
      const phone = msg.data.session_id.split('_').slice(1).join('_');
      notifyNewCase('SARA escaló un caso', `${phone}${msg.data.reason ? ' · ' + msg.data.reason : ''}`);
      setToast({ text: `SARA escaló un caso: ${phone}`, session_id: msg.data.session_id });
      refreshRef.current();
      return;
    }
    if (['conversation_assigned', 'conversation_closed', 'conversation_requeued'].includes(msg.type)) {
      const d = msg.data;
      const me = userRef.current;
      if (msg.type === 'conversation_assigned' && d.to_user.id === me?.id && d.by?.id !== me?.id) {
        const phone = d.session_id.split('_').slice(1).join('_');
        if (d.transfer) {
          notifyNewCase('Caso transferido', `${phone} · de ${d.by?.name || 'un compañero'}${d.note ? `: ${d.note}` : ''}`);
          setToast({ text: `Caso transferido por ${d.by?.name || 'un compañero'}: ${phone}`, session_id: d.session_id });
        } else {
          notifyNewCase('Nuevo caso asignado', `${phone} · asignado por ${d.by?.name || 'el sistema'}`);
          setToast({ text: `Nuevo caso asignado: ${phone}`, session_id: d.session_id });
        }
      }
      // Si el asesor perdió el caso abierto (reasignado o cerrado), se cierra la vista
      const lostIt = me?.role !== 'admin' && selectedRef.current?.session_id === d.session_id &&
        (msg.type !== 'conversation_assigned' || d.to_user.id !== me?.id);
      if (lostIt) { setSelected(null); setMessages([]); }
      refreshRef.current();
      return;
    }
    if (msg.type === 'contact_updated') {
      const { session_id, contact_name, contact_name_source } = msg.data;
      setConversations(prev => prev.map(c => (c.session_id === session_id ? { ...c, contact_name, contact_name_source } : c)));
      setSelected(prev => (prev?.session_id === session_id ? { ...prev, contact_name, contact_name_source } : prev));
      return;
    }
    if (msg.type === 'conversation_read') {
      // Lo leí en otra pestaña
      setUnread(prev => (prev[msg.data.session_id] ? { ...prev, [msg.data.session_id]: 0 } : prev));
      return;
    }
    if (msg.type === 'conversation_archived') {
      // Archivado o recuperado (en esta u otra pestaña): sale de la vista donde ya no va
      const { session_id, archived } = msg.data;
      if (archived !== showArchivedRef.current) {
        dropFromList(session_id);
        if (selectedRef.current?.session_id === session_id) { setSelected(null); setMessages([]); }
      } else {
        refreshListRef.current();
      }
      return;
    }
    if (msg.type === 'send_failed') {
      const fm = msg.data;
      if (selectedRef.current?.session_id === fm.session_id) {
        setMessages(prev => prev.some(m => m.failed_id === fm.failed_id) ? prev : [...prev, fm]);
      }
      return;
    }
    if (msg.type !== 'new_message') return;
    const nm = msg.data;
    // Mensaje del cliente (los de SARA son 'ai' y los del portal 'asesor')
    const inbound = nm.type === 'human';

    // En Archivados, un chat al que el cliente le vuelve a escribir regresa a la bandeja
    if (inbound && showArchivedRef.current && selectedRef.current?.session_id !== nm.session_id) {
      dropFromList(nm.session_id);
      return;
    }

    setConversations(prev => {
      const idx = prev.findIndex(c => c.session_id === nm.session_id);
      const updated = idx >= 0 ? [...prev] : [...prev];
      if (idx >= 0) {
        const c = updated[idx];
        updated[idx] = {
          ...c, last_message: nm.content, last_type: nm.type, last_at: nm.created_at,
          // Un mensaje del cliente abre la ventana de 24 h de WhatsApp
          ...(inbound ? { window_open: true } : {}),
          // Espera: empieza con el primer mensaje del cliente y termina con la respuesta
          waiting_since: inbound ? (c.waiting_since || nm.created_at) : null,
        };
        const [conv] = updated.splice(idx, 1);
        return [conv, ...updated];
      }
      return prev;
    });
    // Conversación que no está en las páginas cargadas (o archivada): sube al refrescar
    // la primera página (máximo una vez cada 3 s: los técnicos reciben todos los mensajes
    // de SARA). Si el cliente le escribió a un caso mío, se avisa al aparecer.
    if (!convsRef.current.some(c => c.session_id === nm.session_id)) {
      if (inbound || Date.now() - lastListRefresh.current > 3000) {
        lastListRefresh.current = Date.now();
        refreshListRef.current()?.then(data => {
          const back = data?.find(c => c.session_id === nm.session_id);
          if (inbound && isMine(back)) alertReply(back, false);
        });
      }
      return;
    }

    const isOpen = selectedRef.current?.session_id === nm.session_id;
    if (isOpen) {
      // Un mismo mensaje puede llegar dos veces (respuesta de /send y poller)
      setMessages(prev => prev.some(m => m.id === nm.id) ? prev : [...prev, nm]);
    }

    // El cliente respondió en un caso mío: aviso como WhatsApp, salvo que lo esté leyendo
    const conv = convsRef.current.find(c => c.session_id === nm.session_id);
    if (!inbound || !isMine(conv)) return;
    if (isOpen && !document.hidden) { markReadRef.current(nm.session_id); return; }
    setUnread(prev => ({ ...prev, [nm.session_id]: (prev[nm.session_id] || 0) + 1 }));
    alertReply(conv, isOpen);

    function alertReply(c, open) {
      const name = c.contact_name || c.user_id;
      notifyReply(nm.session_id, `${name} respondió`, (nm.content || '').slice(0, 140),
        () => selectConvRef.current(convsRef.current.find(x => x.session_id === nm.session_id) || c));
      if (!open) setToast({ text: `${name} respondió`, session_id: nm.session_id });
    }
  }, [isMine, dropFromList]));

  // Al volver a la pestaña con un caso mío abierto, lo que llegó queda leído
  useEffect(() => {
    function onVisible() {
      const sel = selectedRef.current;
      if (document.hidden || !sel) return;
      if (isMine(convsRef.current.find(c => c.session_id === sel.session_id))) markReadRef.current(sel.session_id);
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isMine]);

  // Título de la pestaña con los chats por leer: "(3) Portal"
  const baseTitle = useRef(document.title);
  const unreadChats = Object.values(unread).filter(n => n > 0).length;
  useEffect(() => {
    document.title = unreadChats ? `(${unreadChats}) ${baseTitle.current}` : baseTitle.current;
  }, [unreadChats]);
  useEffect(() => () => { document.title = baseTitle.current; }, []);

  // ─── Auto scroll ──────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ─── Seleccionar conversación ─────────────────────────────────────────────
  function selectConv(conv) {
    setSelected(conv);
    setSendError('');
    if (isMine(conv)) markRead(conv.session_id);
    else setUnread(prev => (prev[conv.session_id] ? { ...prev, [conv.session_id]: 0 } : prev));
  }
  const selectConvRef = useRef(selectConv);
  selectConvRef.current = selectConv;

  // ─── Enviar mensaje ───────────────────────────────────────────────────────
  async function sendMessage() {
    if (!inputText.trim() || !selected || sending || windowClosed) return;
    setSending(true);
    setSendError('');
    try {
      await apiFetch('/api/send', {
        method: 'POST',
        body: JSON.stringify({
          session_id: selected.session_id,
          text: inputText.trim(),
        }),
      });
      setInputText('');
      textareaRef.current?.focus();
    } catch (e) {
      // El mensaje fallido aparece en el chat (evento send_failed); aquí se deja el aviso
      // y se conserva el texto para reintentar.
      setSendError(e.message);
      if (e.code === 'WINDOW_CLOSED') {
        setConversations(prev => prev.map(c => c.session_id === selected.session_id ? { ...c, window_open: false } : c));
      }
    } finally {
      setSending(false);
    }
  }

  // ─── Enviar imagen (botón +) o promoción de la biblioteca ─────────────────
  // El mensaje aparece en el chat por la respuesta/WebSocket, igual que el texto.
  function imageSendFailed(e) {
    if (e.code === 'WINDOW_CLOSED') {
      setConversations(prev => prev.map(c => c.session_id === selected.session_id ? { ...c, window_open: false } : c));
    }
    throw e;
  }
  async function sendImage(file, caption) {
    const fd = new FormData();
    fd.append('file', file);
    if (caption) fd.append('caption', caption);
    try {
      const r = await apiFetch(`/api/conversations/${selected.session_id}/images`, { method: 'POST', body: fd });
      setMessages(prev => prev.some(m => m.id === r.message.id) ? prev : [...prev, r.message]);
    } catch (e) { imageSendFailed(e); }
  }
  async function sendPromotion(promotionId, caption) {
    try {
      const r = await apiFetch(`/api/conversations/${selected.session_id}/promotions/${promotionId}/send`, {
        method: 'POST', body: JSON.stringify({ caption }),
      });
      setMessages(prev => prev.some(m => m.id === r.message.id) ? prev : [...prev, r.message]);
    } catch (e) { imageSendFailed(e); }
  }

  // Recarga lista y carga tras un cambio de asignación (también desde el WebSocket)
  refreshRef.current = () => { loadConversations(null, { reset: false }); loadAssignable(); };

  // ─── Nombre del contacto ──────────────────────────────────────────────────
  async function saveContactName(session_id, name) {
    const r = await apiFetch(`/api/conversations/${session_id}/contact`, { method: 'PUT', body: JSON.stringify({ name }) });
    setConversations(prev => prev.map(c => (c.session_id === session_id ? { ...c, ...r } : c)));
    setSelected(prev => (prev?.session_id === session_id ? { ...prev, ...r } : prev));
    return r;
  }

  // ─── Transferir (punto 1) y asesor anterior (punto 5) ──────────────────────
  async function doTransfer(session_id, user_id, note) {
    await apiFetch(`/api/conversations/${session_id}/transfer`, { method: 'POST', body: JSON.stringify({ user_id, note }) });
    // El asesor que transfiere deja de ver el caso; el admin lo sigue viendo
    if (!isAdmin) { setSelected(null); setMessages([]); }
    setToast({ text: 'Caso transferido', session_id: null });
    refreshRef.current();
  }

  async function setPreferred(session_id, user_id) {
    try {
      await apiFetch(`/api/conversations/${session_id}/preferred-agent`, { method: 'PUT', body: JSON.stringify({ user_id }) });
      refreshRef.current();
    } catch (e) {
      alert('No se pudo cambiar el asesor anterior: ' + e.message);
    }
  }

  // ─── Asignación ───────────────────────────────────────────────────────────
  async function assignConv(session_id, user_id) {
    try {
      await apiFetch(`/api/conversations/${session_id}/assign`, { method: 'POST', body: JSON.stringify({ user_id }) });
      refreshRef.current();
    } catch (e) {
      alert('No se pudo asignar: ' + e.message);
    }
  }

  async function closeConv(session_id) {
    if (!window.confirm('¿Cerrar este caso y devolver la conversación a SARA? SARA volverá a responder al cliente.')) return;
    try {
      await apiFetch(`/api/conversations/${session_id}/close`, { method: 'POST' });
      if (!isAdmin) { setSelected(null); setMessages([]); }
      refreshRef.current();
    } catch (e) {
      alert('No se pudo cerrar: ' + e.message);
    }
  }

  // Archivar (personal): sale de la bandeja y vuelve cuando el cliente escribe
  async function archiveConv(session_id, archive) {
    try {
      await apiFetch(`/api/conversations/${session_id}/archive`, { method: archive ? 'POST' : 'DELETE' });
      dropFromList(session_id);
      if (selectedRef.current?.session_id === session_id) { setSelected(null); setMessages([]); }
      setToast({ text: archive ? 'Chat archivado: vuelve a tu bandeja cuando el cliente escriba' : 'Chat devuelto a tu bandeja' });
    } catch (e) {
      alert('No se pudo archivar: ' + e.message);
    }
  }

  // Cola (V1) o, para asesores técnicos, una conversación que atiende SARA ("Atender yo")
  async function takeConv(session_id, from = 'queue') {
    setTaking(true);
    try {
      await apiFetch(`/api/conversations/${session_id}/${from === 'sara' ? 'attend' : 'take'}`, { method: 'POST' });
      await loadConversations();
      loadMessages(session_id);
    } catch (e) {
      alert(e.message);
      // Otro asesor la tomó o SARA ya la escaló: el asesor deja de verla
      if (!isAdmin && from === 'sara') { setSelected(null); setMessages([]); }
      refreshRef.current();
    } finally {
      setTaking(false);
    }
  }

  // ─── Presencia: Disponible / Pausa ────────────────────────────────────────
  async function togglePresence() {
    const next = myPresence === 'paused' ? 'online' : 'paused';
    try {
      const r = await apiFetch('/api/presence', { method: 'POST', body: JSON.stringify({ status: next }) });
      setMyPresence(r.status);
    } catch (e) {
      alert('No se pudo cambiar tu estado: ' + e.message);
    }
  }

  // ─── Borrar conversación completa ─────────────────────────────────────────
async function deleteConversation(session_id) {
  if (!window.confirm('¿Borrar toda esta conversación? Esta acción no se puede deshacer.')) return;
  try {
    await apiFetch(`/api/conversations/${session_id}`, { method: 'DELETE' });
    setConversations(prev => prev.filter(c => c.session_id !== session_id));
    if (selected?.session_id === session_id) {
      setSelected(null);
      setMessages([]);
    }
  } catch (e) {
    alert('Error al borrar: ' + e.message);
  }
}

// ─── Borrar mensaje individual ────────────────────────────────────────────
async function deleteMessage(session_id, msg_id) {
  if (!window.confirm('¿Borrar este mensaje?')) return;
  try {
    await apiFetch(`/api/conversations/${session_id}/messages/${msg_id}`, { method: 'DELETE' });
    setMessages(prev => prev.filter(m => m.id !== msg_id));
  } catch (e) {
    alert('Error al borrar mensaje: ' + e.message);
  }
}

// ─── Tags ──────────────────────────────────────────────────────────────────
  async function addTag(session_id, tag_id) {
    try {
      await apiFetch(`/api/conversations/${session_id}/tags`, {
        method: 'POST',
        body: JSON.stringify({ tag_id }),
      });
      setConversations(prev => prev.map(c => {
        if (c.session_id !== session_id) return c;
        const tag = allTags.find(t => t.id === tag_id);
        if (c.tags?.some(t => t.id === tag_id)) return c;
        return { ...c, tags: [...(c.tags || []), tag] };
      }));
    } catch (e) {
      alert('Error al etiquetar: ' + e.message);
    }
    setTagPickerFor(null);
  }

  async function removeTag(session_id, tag_id) {
    try {
      await apiFetch(`/api/conversations/${session_id}/tags/${tag_id}`, { method: 'DELETE' });
      setConversations(prev => prev.map(c => {
        if (c.session_id !== session_id) return c;
        return { ...c, tags: (c.tags || []).filter(t => t.id !== tag_id) };
      }));
    } catch (e) {
      alert('Error al quitar etiqueta: ' + e.message);
    }
  }

  // Filtros activos, para el contador y los chips que se quitan con ✕
  const activeFilters = [
    showArchived && { key: 'archived', label: 'Archivados', clear: () => setShowArchived(false) },
    statusFilter && { key: 'status', label: STATUS[statusFilter]?.label || statusFilter, clear: () => setStatusFilter('') },
    tagFilter && (() => {
      const t = allTags.find(x => x.id === tagFilter);
      return { key: 'tag', label: t ? `${t.emoji || ''} ${t.name}`.trim() : 'Etiqueta', clear: () => setTagFilter(null) };
    })(),
    (appliedDates.desde || appliedDates.hasta) && {
      key: 'date', label: `${appliedDates.desde || '…'} → ${appliedDates.hasta || '…'}`, clear: clearDateFilter,
    },
  ].filter(Boolean);

  // ─── Filtrar ──────────────────────────────────────────────────────────────
  // Mis casos con el cliente esperando respuesta van arriba, el que más lleva
  // esperando primero (solo al mostrar: la paginación sigue el orden del servidor)
  const waitingMine = c => isMine(c) && !!c.waiting_since;
  const filtered = [...conversations].sort((a, b) => {
    const wa = waitingMine(a), wb = waitingMine(b);
    if (wa && wb) return new Date(a.waiting_since) - new Date(b.waiting_since);
    return wa ? -1 : wb ? 1 : 0;
  });

  return (
    <div style={s.root}>
      {/* ── Sidebar ── */}
      <aside style={s.sidebar}>
        {/* Header sidebar */}
        <div style={s.sidebarTop}>
          <div style={s.brand}>
            <div style={s.brandLogo}>EAS</div>
            <div style={s.brandInfo}>
              <div style={s.brandName}>Portal Atención</div>
              <div style={s.brandUser} title={user?.name}>{user?.name}{isAdmin ? ' · Admin' : ''}</div>
            </div>
            {myPresence && (
              <button
                onClick={togglePresence}
                style={{ ...s.presenceBtn, marginTop: 0, ...(myPresence === 'paused' ? s.presencePaused : s.presenceOnline) }}
                title={myPresence === 'paused'
                  ? 'En Pausa: sigues atendiendo tus chats, pero no recibes casos nuevos. Clic para ponerte Disponible'
                  : 'Disponible: recibes casos nuevos. Clic para ponerte en Pausa'}
              >
                <span style={s.presenceDot} />
                {myPresence === 'paused' ? 'En pausa' : 'Disponible'}
              </button>
            )}
          </div>

          <div style={s.toolbar}>
            {onOpenAdmin && <button className="ui-hover" onClick={onOpenAdmin} style={s.toolBtn} title="Usuarios, cola, plantillas, métricas y supervisión"><Users size={14} /> Admin</button>}
            {onOpenStudio && <button className="ui-hover" onClick={onOpenStudio} style={s.toolBtn} title="Supervisión en vivo"><Radio size={14} /> Estudio</button>}
            <button className="ui-hover" onClick={() => setTemplateModal({})} style={s.toolBtn} title="Nueva conversación con plantilla"><Mail size={14} /> Nueva</button>
          </div>

          {/* Búsqueda + botón de filtros */}
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ ...s.search, flex: 1 }}>
              <Search size={15} />
              <input
                style={s.searchInput}
                placeholder="Buscar nombre o número"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {search && <button onClick={() => setSearch('')} style={s.searchClear} aria-label="Limpiar búsqueda"><X size={14} /></button>}
            </div>
            <button
              className="ui-hover"
              onClick={toggleFilters}
              style={{ ...s.filterBtn, ...(filtersOpen || activeFilters.length ? s.filterBtnActive : {}) }}
              title={filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'}
              aria-expanded={filtersOpen}
              aria-controls="panel-filtros"
            >
              <SlidersHorizontal size={15} />
              Filtros
              {activeFilters.length > 0 && <span style={s.filterCount}>{activeFilters.length}</span>}
              <ChevronDown size={14} style={{ transition: 'transform 0.15s', transform: filtersOpen ? 'rotate(180deg)' : 'none' }} />
            </button>
          </div>

          {/* Filtros activos (cuando el panel está cerrado): se quitan con ✕ */}
          {!filtersOpen && activeFilters.length > 0 && (
            <div style={s.activeRow}>
              {activeFilters.map(f => (
                <span key={f.key} style={s.activeChip}>
                  {f.label}
                  <button onClick={f.clear} style={s.activeChipX} aria-label={`Quitar filtro ${f.label}`}><X size={12} /></button>
                </span>
              ))}
            </div>
          )}

          {/* Panel de filtros */}
          {filtersOpen && (
            <div id="panel-filtros" style={s.filtersPanel}>
              <div>
                <div style={s.filterLabel}>Bandeja</div>
                <div style={s.statusFilterRow}>
                  {[[false, 'Principal'], [true, 'Archivados']].map(([value, label]) => (
                    <button
                      key={label}
                      onClick={() => setShowArchived(value)}
                      style={{ ...s.tagFilterChip, ...(showArchived === value ? s.tagFilterChipActive : {}) }}
                      aria-pressed={showArchived === value}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {(isAdmin || seesSara) && (
                <div>
                  <div style={s.filterLabel}>Estado</div>
                  <div style={s.statusFilterRow}>
                    {(isAdmin
                      ? [['', 'Todas'], ...Object.entries(STATUS).map(([k, v]) => [k, v.label])]
                      : [['', 'Todas'], ['assigned', 'Mis casos'], ['bot', STATUS.bot?.label || 'SARA']]
                    ).map(([key, label]) => (
                      <button
                        key={key || 'all'}
                        onClick={() => setStatusFilter(key)}
                        style={{ ...s.tagFilterChip, ...(statusFilter === key ? s.tagFilterChipActive : {}) }}
                        aria-pressed={statusFilter === key}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {allTags.length > 0 && (
                <div>
                  <div style={s.filterLabel}>Etiqueta</div>
                  <div style={s.statusFilterRow}>
                    <button
                      onClick={() => setTagFilter(null)}
                      style={{ ...s.tagFilterChip, ...(tagFilter === null ? s.tagFilterChipActive : {}) }}
                      aria-pressed={tagFilter === null}
                    >
                      Todas
                    </button>
                    {allTags.map(tag => (
                      <button
                        key={tag.id}
                        onClick={() => setTagFilter(tagFilter === tag.id ? null : tag.id)}
                        style={{
                          ...s.tagFilterChip,
                          ...(tagFilter === tag.id ? { background: tag.color + '30', border: `1px solid ${tag.color}`, color: 'var(--text)' } : {}),
                        }}
                        aria-pressed={tagFilter === tag.id}
                      >
                        {tag.emoji} {tag.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div style={s.filterLabel}>Fecha</div>
                <div style={s.dateRow}>
                  <label style={s.dateLabel} htmlFor="f-desde">Desde</label>
                  <input id="f-desde" type="date" style={s.dateInput} value={dateFilter.desde}
                    onChange={e => setDateFilter(f => ({ ...f, desde: e.target.value }))} />
                </div>
                <div style={{ ...s.dateRow, marginTop: 6 }}>
                  <label style={s.dateLabel} htmlFor="f-hasta">Hasta</label>
                  <input id="f-hasta" type="date" style={s.dateInput} value={dateFilter.hasta}
                    onChange={e => setDateFilter(f => ({ ...f, hasta: e.target.value }))} />
                </div>
                <div style={s.dateActions}>
                  <button className="ui-primary" style={s.dateApplyBtn} onClick={() => applyDateFilter(dateFilter.desde, dateFilter.hasta)}>
                    Aplicar fechas
                  </button>
                </div>
              </div>

              <div style={s.filtersFoot}>
                <button className="ui-hover" style={s.dateClearBtn} onClick={clearAllFilters} disabled={activeFilters.length === 0}>
                  Limpiar todo
                </button>
                <button className="ui-hover" style={s.dateClearBtn} onClick={toggleFilters}>Cerrar</button>
              </div>
            </div>
          )}
        </div>

        {/* Lista de conversaciones */}
        <div style={s.listHeader}>
          {total != null
            ? `${total.toLocaleString('es-CO')} ${serverQ ? (total === 1 ? 'resultado' : 'resultados') : (total === 1 ? 'conversación' : 'conversaciones')}`
            : ''}
        </div>
        <ConversationList
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
          conversations={filtered}
          selectedId={selected?.session_id}
          unread={unread}
          currentUserId={user?.id}
          replyAlertMinutes={replyAlertMinutes}
          isAdmin={isAdmin}
          search={search}
          onSelect={selectConv}
          onDelete={deleteConversation}
        />

        {/* Pie: conexión en vivo, tema, clave y salir */}
        <div style={s.wsBar}>
          <span style={{ ...s.wsDot, background: wsStatus === 'connected' ? 'var(--success)' : wsStatus === 'connecting' ? 'var(--warning)' : 'var(--danger)' }} />
          <span style={{ ...s.wsLabel, flex: 1 }}>
            {wsStatus === 'connected' ? 'En vivo' : wsStatus === 'connecting' ? 'Conectando…' : 'Sin conexión'}
          </span>
          <ThemeToggle style={s.iconBtn} />
          <button className="ui-hover" onClick={onChangePassword} style={s.iconBtn} title="Cambiar contraseña" aria-label="Cambiar contraseña"><KeyRound size={15} /></button>
          <button className="ui-hover" onClick={logout} style={s.logoutBtn} title="Cerrar sesión" aria-label="Cerrar sesión"><LogOut size={15} /></button>
        </div>
      </aside>

      {templateModal && (
        <StartConversationModal
          fixedPhone={templateModal.phone}
          onClose={() => setTemplateModal(null)}
          onStarted={async (r) => {
            setTemplateModal(null);
            await loadConversations();
            // Abrir la conversación si quedó a mi cargo (o soy admin)
            setSelected(prev => (prev?.session_id === r.session_id ? { ...prev } : { session_id: r.session_id, canal: 'whatsapp', user_id: r.session_id.split('_')[1], status: r.status }));
            loadMessages(r.session_id);
          }}
        />
      )}

      {transferConv && (
        <TransferModal conv={transferConv} currentAssigneeId={transferConv.assigned_user?.id}
          onClose={() => setTransferConv(null)} onTransfer={doTransfer} />
      )}

      {toast && (
        <div style={s.toast} onClick={() => {
          const conv = conversations.find(c => c.session_id === toast.session_id);
          if (conv) selectConv(conv);
          setToast(null);
        }}>
          <Bell size={16} color="var(--accent)" />
          <span style={{ flex: 1 }}>{toast.text}</span>
          {toast.session_id && <u style={{ color: 'var(--accent-text)', fontWeight: 700 }}>Abrir</u>}
        </div>
      )}

      {/* ── Panel principal ── */}
      <main style={s.main}>
        {!selected ? (
          <EmptyState />
        ) : selectedConv.status === 'queued' && !isAdmin ? (
          <QueuedPanel conv={selectedConv} onTake={takeConv} taking={taking} />
        ) : (
          <>
            <ConversationView
              conv={selectedConv}
              messages={messages}
              loading={loadingMsgs}
              isAdmin={isAdmin}
              currentUserId={user?.id}
              canClose={canOperate}
              users={assignableUsers}
              onAssign={assignConv}
              onAttend={!isAdmin ? id => takeConv(id, 'sara') : undefined}
              attending={taking}
              onClose={closeConv}
              onDeleteMessage={deleteMessage}
              messagesEndRef={messagesEndRef}
              allTags={allTags}
              tagPickerOpen={tagPickerFor === selectedConv.session_id}
              // Un técnico que solo está mirando un chat de SARA no lo edita
              onToggleTagPicker={canOperate ? id => setTagPickerFor(tagPickerFor === id ? null : id) : undefined}
              onAddTag={addTag}
              onRemoveTag={removeTag}
              onSaveName={canOperate ? saveContactName : undefined}
              onTransfer={c => setTransferConv(c)}
              onSetPreferred={setPreferred}
              onArchive={archiveConv}
            />
            <Composer
              conv={selectedConv}
              text={inputText}
              onChange={setInputText}
              onSend={sendMessage}
              sending={sending}
              sendError={sendError}
              windowClosed={windowClosed}
              textareaRef={textareaRef}
              onSendTemplate={() => setTemplateModal({ phone: selectedConv.user_id })}
              isAdmin={isAdmin}
              agentName={user?.name}
              onSendImage={sendImage}
              onSendPromotion={sendPromotion}
            />
          </>
        )}
      </main>
    </div>
  );
}
